import { useCallback, useEffect, useState } from "react";
import { Plus, Check, X, Lock, Image } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fmtDate, apiError, logError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SecuritySection } from "../components/SecuritySection";
import { LoginHistory } from "../components/LoginHistory";
import { CurrencySection } from "../components/CurrencySection";

const FEATURE_LABELS = {
  fraud_alerts: "Alertes anti-fraude",
  ai: "Assistant IA & résumé quotidien",
  ai_proactive: "IA proactive",
  hr: "RH (salaires, primes, heures sup.)",
  custom_permissions: "Permissions module par module",
  role_presets: "Rôles prédéfinis (Gestionnaire / Vendeur)",
  dashboard_details: "Vue détaillée « Voir plus »",
  multi_establishment: "Multi-établissements",
  carton_unit: "Conversion carton / unité",
  remove_branding: "Retirer « Généré via Kavizio » des documents",
};
const PAY_LABELS = { trial: "Essai", paid: "À jour", late: "En retard" };

function SubscriptionCard({ sub }) {
  const current = sub.catalog.find((t) => t.key === sub.current);
  return (
    <div className="kcard p-5 space-y-4" data-testid="subscription-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold">Abonnement</h3>
          <p className="text-sm text-gray-600">
            Palier actuel : <span className="font-semibold" data-testid="subscription-tier">{sub.label}</span> · {sub.price_label}
          </p>
          <p className="text-xs text-gray-500">
            {sub.users_used} / {sub.max_users ?? "illimité"} utilisateur(s) · factures : {sub.invoice_quota ?? "illimitées"}{sub.invoice_quota ? "/mois" : ""} · ticket illimité
          </p>
        </div>
        <span className="badge-partial px-2.5 py-1 rounded-full text-xs font-semibold">Paiement : {PAY_LABELS[sub.payment_status] || sub.payment_status}</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-1.5">
        {Object.entries(FEATURE_LABELS).map(([k, l]) => {
          const on = current?.features?.includes(k);
          return (
            <div key={k} data-testid={`feature-${k}`} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-xl bg-[#F6F4EF]/60">
              {on ? <Check size={15} weight="bold" color="#2E7D5B" /> : <X size={15} weight="bold" color="#B4432F" />}
              <span className={on ? "" : "text-gray-400"}>{l}</span>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-gray-500">Pour changer de palier, contactez Kavizio. Les nouveaux modules apparaissent immédiatement, sans perte de données.</p>
    </div>
  );
}

export default function Settings() {
  const { user, setUser, has, refresh } = useAuth();
  const [company, setCompany] = useState(null);
  const [sub, setSub] = useState(null);
  const [ests, setEsts] = useState([]);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [newEst, setNewEst] = useState({ name: "", address: "" });
  const [showEst, setShowEst] = useState(false);
  const multiEst = has("multi_establishment");
  const canRemoveBranding = has("remove_branding");
  const [logoUrl, setLogoUrl] = useState("");

  const loadLogo = useCallback(() => {
    api.get("/company/logo", { responseType: "blob" })
      .then((r) => setLogoUrl(URL.createObjectURL(r.data)))
      .catch(() => setLogoUrl(""));
  }, []);
  useEffect(() => { loadLogo(); }, [loadLogo]);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo enregistré — il apparaîtra sur vos tickets et factures");
      loadLogo();
      setCompany((c) => ({ ...c, logo_path: "set" }));
    } catch (err) { toast.error(apiError(err)); }
  };

  const load = useCallback(() => {
    api.get("/company").then((r) => setCompany(r.data)).catch((e) => logError(e));
    api.get("/subscription").then((r) => setSub(r.data)).catch((e) => logError(e));
    api.get("/establishments").then((r) => setEsts(r.data)).catch((e) => logError(e));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (updates) => {
    try {
      await api.patch("/company", updates);
      toast.success("Enregistré");
      const c = { ...company, ...updates };
      setCompany(c);
      setUser({ ...user, company: c });
    } catch (e) { toast.error(apiError(e)); }
  };

  const addEst = async () => {
    try {
      await api.post("/establishments", newEst);
      toast.success("Établissement créé");
      setShowEst(false); setNewEst({ name: "", address: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadAudit = () => {
    api.get("/audit").then((r) => { setAudit(r.data); setShowAudit(true); }).catch(() => {});
  };

  if (!company) return <p className="text-sm text-gray-400 py-10">Chargement...</p>;

  return (
    <div className="max-w-2xl space-y-4 fade-in" data-testid="settings-page">
      <h1 className="font-display font-bold text-2xl">Paramètres</h1>

      {sub && <SubscriptionCard sub={sub} />}

      <SecuritySection />

      <LoginHistory />

      <CurrencySection current={company.currency} onChanged={refresh} />

      <div className="kcard p-5 space-y-3">
        <h3 className="font-display font-bold">Informations entreprise</h3>
        <p className="text-xs text-gray-500">Renseignées une seule fois — elles apparaissent sur chaque ticket et chaque facture.</p>
        <input data-testid="settings-name-input" className="kinput" placeholder="Nom de l'entreprise" value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} onBlur={(e) => save({ name: e.target.value })} />
        <input data-testid="settings-address-input" className="kinput" placeholder="Adresse / quartier" value={company.address || ""} onChange={(e) => setCompany({ ...company, address: e.target.value })} onBlur={(e) => save({ address: e.target.value })} />
        <input data-testid="settings-business-number-input" className="kinput" placeholder="N° d'entreprise (RCCM)" value={company.business_number || ""} onChange={(e) => setCompany({ ...company, business_number: e.target.value })} onBlur={(e) => save({ business_number: e.target.value })} />
        <input data-testid="settings-phone-input" className="kinput" placeholder="Téléphone de l'entreprise" value={company.phone || ""} onChange={(e) => setCompany({ ...company, phone: e.target.value })} onBlur={(e) => save({ phone: e.target.value })} />
        <div>
          <p className="text-xs text-gray-500 mb-1">Note par défaut des factures, devis et bons de livraison (jamais sur le ticket de caisse)</p>
          <textarea data-testid="settings-document-note-input" className="kinput" rows={3} maxLength={300}
            placeholder="Ex. coordonnées bancaires, conditions de paiement, mentions légales"
            value={company.document_note || ""}
            onChange={(e) => setCompany({ ...company, document_note: e.target.value })}
            onBlur={(e) => save({ document_note: e.target.value })} />
        </div>
        <div className="flex items-center gap-3 flex-wrap pt-1">
          {logoUrl && <img data-testid="settings-logo-preview" src={logoUrl} alt="Logo de l'entreprise" className="w-14 h-14 rounded-xl object-contain bg-[#F6F4EF]" />}
          <label className="btn-ghost py-2 px-3 text-xs font-semibold flex items-center gap-1.5 cursor-pointer">
            <Image size={14} /> {company.logo_path ? "Changer le logo" : "Ajouter un logo"}
            <input data-testid="settings-logo-input" type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp" onChange={uploadLogo} />
          </label>
          <span className="text-xs text-gray-500">JPG, PNG ou WEBP, 2 Mo maximum. Le SVG est refusé.</span>
        </div>
      </div>

      <div className="kcard p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold">Mention « Généré via Kavizio »</h3>
            <p className="text-xs text-gray-500">
              {canRemoveBranding
                ? "Vous pouvez retirer cette mention du bas de vos tickets et factures."
                : "Retirer cette mention est réservé aux paliers Business et Sur mesure."}
            </p>
          </div>
          {canRemoveBranding ? (
            <button data-testid="toggle-branding-btn" onClick={() => save({ hide_kavizio_mention: !company.hide_kavizio_mention })}
              className={`py-1.5 px-3 rounded-full text-xs font-semibold ${company.hide_kavizio_mention ? "badge-unpaid" : "badge-paid"}`}>
              {company.hide_kavizio_mention ? "Mention retirée" : "Mention affichée"}
            </button>
          ) : (
            <span data-testid="branding-locked" className="text-xs text-gray-500 flex items-center gap-1"><Lock size={13} /> Palier Business</span>
          )}
        </div>
      </div>

      <div className="kcard p-5">
        <h3 className="font-display font-bold mb-1">Couleur des documents</h3>
        <p className="text-xs text-gray-500 mb-3">
          Appliquée à la facture, au devis et au bon de livraison (barre du haut, numéro, en-tête du tableau).
          « Noir & blanc » économise l'encre à l'impression.
        </p>
        <div className="flex flex-wrap gap-2">
          {[["amber", "#C9793A", "Ambre"], ["navy", "#16233D", "Bleu marine"], ["green", "#2E7D5B", "Vert"],
            ["red", "#B4432F", "Rouge"], ["violet", "#674D92", "Violet"], ["teal", "#25757A", "Bleu-vert"],
            ["mono", "#1F1F1F", "Noir & blanc"]].map(([key, hex, label]) => (
            <button key={key} data-testid={`doc-accent-${key}`} onClick={() => save({ doc_accent: key })}
              title={label}
              className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border transition-colors min-h-[40px] ${(company.doc_accent || "amber") === key ? "border-[#16233D] bg-[#F6F4EF]" : "border-[#E0DACE] bg-white"}`}>
              <span className="w-6 h-6 rounded-full shrink-0" style={{ backgroundColor: hex }} />
              <span className="text-xs">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="kcard p-5">
        <h3 className="font-display font-bold mb-1">Arrondi du prix final</h3>
        <p className="text-xs text-gray-500 mb-3">
          Quand une remise est saisie en pourcentage, le prix final est arrondi à l'unité choisie.
          Une remise saisie en montant n'est jamais arrondie.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {[[0, "Aucun"], [1000, "1 000 GNF"], [5000, "5 000 GNF"], [10000, "10 000 GNF"]].map(([v, l]) => (
            <button key={v} data-testid={`price-rounding-${v}`} onClick={() => save({ price_rounding: v })}
              className={`text-left p-3.5 rounded-2xl border transition-colors ${(company.price_rounding || 0) === v ? "border-[#C9793A] bg-[#C9793A]/5" : "border-[#E0DACE] bg-white"}`}>
              <span className="text-sm font-semibold block">{l}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="kcard p-5">
        <h3 className="font-display font-bold mb-2">Mode de gestion du stock</h3>
        <div className="grid grid-cols-2 gap-2">
          {[["with_stock", "Avec stock", "Ventes détaillées produit par produit"], ["no_stock", "Sans stock", "Montant global, stock désactivé"]].map(([v, t, d]) => (
            <button key={v} data-testid={`settings-stock-mode-${v}`} onClick={() => save({ stock_mode: v })}
              className={`text-left p-3.5 rounded-2xl border transition-colors ${company.stock_mode === v ? "border-[#C9793A] bg-[#C9793A]/5" : "border-[#E0DACE] bg-white"}`}>
              <span className="text-sm font-semibold block">{t}</span>
              <span className="text-xs text-gray-500">{d}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="kcard p-5">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="font-display font-bold">Établissements</h3>
          {multiEst ? (
            <button data-testid="add-establishment-btn" onClick={() => setShowEst(true)} className="btn-ghost py-1.5 px-3 text-xs flex items-center gap-1"><Plus size={13} /> Ajouter</button>
          ) : (
            <span data-testid="establishment-locked" className="text-xs text-gray-500 flex items-center gap-1"><Lock size={13} /> Multi-établissements : Palier 4</span>
          )}
        </div>
        <div className="space-y-2">
          {ests.map((e) => (
            <div key={e.id} data-testid="establishment-row" className="px-3 py-2.5 rounded-xl bg-[#F6F4EF]/60">
              <p className="text-sm font-semibold">{e.name}</p>
              <p className="text-xs text-gray-500">{e.address || "—"}</p>
            </div>
          ))}
        </div>
        {showEst && (
          <div className="mt-3 space-y-2">
            <input data-testid="establishment-name-input" className="kinput" placeholder="Nom de l'établissement" value={newEst.name} onChange={(e) => setNewEst({ ...newEst, name: e.target.value })} />
            <input data-testid="establishment-address-input" className="kinput" placeholder="Adresse" value={newEst.address} onChange={(e) => setNewEst({ ...newEst, address: e.target.value })} />
            <div className="flex gap-2">
              <button className="btn-ghost flex-1 py-2" onClick={() => setShowEst(false)}>Annuler</button>
              <button data-testid="establishment-create-btn" className="btn-amber flex-1 py-2" disabled={!newEst.name} onClick={addEst}>Créer</button>
            </div>
          </div>
        )}
      </div>

      <div className="kcard p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold">Journal d'activité</h3>
            <p className="text-xs text-gray-500">Toutes les corrections, annulations et modifications d'accès, tracées — y compris celles du patron.</p>
          </div>
          <button data-testid="show-audit-btn" onClick={loadAudit} className="btn-ghost py-1.5 px-3 text-xs">Afficher</button>
        </div>
        {showAudit && (
          <div className="mt-3 space-y-1.5 max-h-96 overflow-y-auto">
            {audit.map((a) => (
              <div key={a.id} data-testid="audit-row" className="px-3 py-2 rounded-xl bg-[#F6F4EF]/60 text-xs">
                <p className="font-semibold">{a.action.replaceAll("_", " ")} <span className="font-normal text-gray-500">· {a.author_name} · {fmtDate(a.created_at)}</span></p>
                {a.old_value && <p className="text-gray-500">Avant : {JSON.stringify(a.old_value)}</p>}
                {a.new_value && <p className="text-gray-500">Après : {JSON.stringify(a.new_value)}</p>}
                {a.reason && <p className="text-[#C99A2E]">Motif : {a.reason}</p>}
              </div>
            ))}
            {audit.length === 0 && <p className="text-sm text-gray-400">Aucune entrée.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
